import { useCallback, useState } from 'react';

type UseControlledStateParams<T> = {
	controlledValue?: T;
	defaultValue: T;
	preferenceKey: string;
	userPreferences: Record<string, any>;
	setUserPreferences: (prefs: Record<string, any>) => void;
	isSynced: boolean;
	onChange?: (value: T) => void;
};

/**
 * Handles controlled and uncontrolled states for form fields.
 */
export function useControlledState<T>({
	controlledValue,
	defaultValue,
	preferenceKey,
	userPreferences,
	setUserPreferences,
	isSynced,
	onChange,
}: UseControlledStateParams<T>) {
	const [uncontrolledValue, setUncontrolledValue] = useState<T>(
		userPreferences[preferenceKey] || defaultValue,
	);

	const value = controlledValue ?? uncontrolledValue;

	const handleChange = useCallback(
		(newValue: T) => {
			onChange?.(newValue);
			if (controlledValue === undefined) {
				setUncontrolledValue(newValue);
			}
			if (isSynced) {
				setUserPreferences({ [preferenceKey]: newValue });
			}
		},
		[controlledValue, isSynced, onChange, preferenceKey, setUserPreferences],
	);

	return [value, handleChange] as const;
}
